/**
 * Farmer Command Center — redesigned dashboard
 *
 * Sections:
 *   TOP    — Dynamic Farming Journey Stepper (full or cycle-only)
 *   SECOND — Financial Snapshot (Credit / Escrow / Insurance / Settlement)
 *   THIRD  — Crop Status (DOC progress, risk indicator, next action)
 *   FOURTH — Alerts Summary (compact, click to open bell panel)
 *
 * Rules:
 *   • KYC card appears only if KYC pending
 *   • Hide onboarding steps for repeat farmers
 *   • Tailwind grid layout, responsive
 *   • No backend modifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Warning,
  VerifiedUser,
  ArrowForward,
  AccountBalance,
  AccountBalanceWallet,
  Shield,
  TrendingUp,
  Agriculture,
  NotificationsActive,
  ErrorOutline,
  InfoOutlined,
  WarningAmber,
  CheckCircleOutline,
  Grass,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import {
  dashboardAPI,
  stockingEventsAPI,
  kycAPI,
  lifecycleAPI,
  creditAPI,
  escrowAPI,
  settlementAPI,
  alertsAPI,
  harvestsAPI,
} from 'services/api';
import { useNavigate } from 'react-router-dom';
import { LIFECYCLE_STEPS } from 'shared/utils/lifecycle';
import LifecycleStepper from 'shared/components/LifecycleStepper';
import NextActionCard from 'shared/components/NextActionCard';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const statusVal = (s) => {
  if (!s) return '';
  if (typeof s === 'string') return s.toLowerCase();
  if (typeof s === 'object' && s.value) return s.value.toLowerCase();
  return String(s).toLowerCase();
};

// Cycle-only steps for repeat farmers (Steps 5–11 renumbered)
const CYCLE_STEPS = [
  { step: 5,  key: 'ask_for_credit',      label: 'Apply Credit' },
  { step: 6,  key: 'credit_active',       label: 'Credit Active' },
  { step: 7,  key: 'stocking',            label: 'Stocking' },
  { step: 8,  key: 'policy_active',       label: 'Policy Active' },
  { step: 9,  key: 'crop_active',         label: 'Crop Active' },
  { step: 10, key: 'harvest',             label: 'Harvest' },
  { step: 11, key: 'settlement_complete', label: 'Settlement' },
];

const calculateDOC = (date) => {
  if (!date) return 0;
  return Math.floor((new Date() - new Date(date)) / 86400000);
};

// ═════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════
const FarmerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lifecycle
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Bulk data
  const [farms, setFarms] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  const [stockingEvents, setStockingEvents] = useState([]);

  // Financial
  const [creditApps, setCreditApps] = useState([]);
  const [activeLoan, setActiveLoan] = useState(null);
  const [escrowBalance, setEscrowBalance] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [harvests, setHarvests] = useState([]);

  // KYC
  const [kycStatus, setKycStatus] = useState(null);

  // Alerts
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);

  // Repeat farmer detection
  const [completedCycles, setCompletedCycles] = useState(0);

  // ── Load Data ───────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      // Phase 1 — parallel fetches
      const [lifecycleRes, bulkRes, creditRes, stockRes, settlementRes, harvestRes, kycRes] =
        await Promise.allSettled([
          lifecycleAPI.getStatus(),
          dashboardAPI.getFarmerBulkData(),
          creditAPI.getApplications(),
          stockingEventsAPI.getAll(),
          settlementAPI.getAll(),
          harvestsAPI.getAll(),
          kycAPI.getStatus(),
        ]);

      // Lifecycle
      if (lifecycleRes.status === 'fulfilled') {
        const lc = lifecycleRes.value.data;
        setCurrentStep(lc.current_step || 1);
        setCompletedSteps(lc.completed_steps || []);
      }

      // Bulk data
      if (bulkRes.status === 'fulfilled') {
        const bd = bulkRes.value.data;
        const userFarms = bd.farms || [];
        const userPonds = userFarms.flatMap((f) => f.ponds || []);
        const userPolicies = bd.policies || [];
        const userClaims = bd.claims || [];
        setFarms(userFarms);
        setPonds(userPonds);
        setPolicies(userPolicies);
        setClaims(userClaims);
      }

      // Credit
      if (creditRes.status === 'fulfilled') {
        const apps = creditRes.value.data?.data || creditRes.value.data || [];
        setCreditApps(apps);
        const active = apps.find((a) => a.status === 'disbursed')
          || apps.find((a) => a.status === 'approved')
          || null;
        setActiveLoan(active);

        // Fetch escrow balance if active loan
        if (active) {
          try {
            const balRes = await escrowAPI.getLedgerBalance(active.id);
            setEscrowBalance(balRes.data);
          } catch {
            setEscrowBalance(null);
          }
        }
      }

      // Stocking events
      if (stockRes.status === 'fulfilled') {
        setStockingEvents(stockRes.value.data || []);
      }

      // Settlements
      const settData = settlementRes.status === 'fulfilled'
        ? (Array.isArray(settlementRes.value?.data) ? settlementRes.value.data : [])
        : [];
      setSettlements(settData);

      // Harvests
      const harvData = harvestRes.status === 'fulfilled'
        ? (Array.isArray(harvestRes.value?.data) ? harvestRes.value.data : [])
        : [];
      setHarvests(harvData);

      // Repeat farmer: count completed settlements
      const completed = settData.filter((s) => statusVal(s.status) === 'completed');
      setCompletedCycles(completed.length);

      // KYC
      if (kycRes.status === 'fulfilled') {
        setKycStatus(kycRes.value.data);
      }

      // Alerts (separate)
      setAlertsLoading(true);
      try {
        const alertRes = await alertsAPI.getUnacknowledged(5);
        setAlerts(alertRes.data || []);
      } catch {
        setAlerts([]);
      } finally {
        setAlertsLoading(false);
      }
    } catch {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived State ─────────────────────────────────────────
  const isRepeatFarmer = completedCycles > 0;
  const cycleNumber = completedCycles + 1;

  // For repeat farmers, calculate step within cycle (steps 5-11)
  const cycleStepCount = CYCLE_STEPS.length; // 7
  const currentCycleStep = isRepeatFarmer
    ? Math.max(1, currentStep - 4) // steps 5-11 → 1-7
    : currentStep;
  const totalSteps = isRepeatFarmer ? cycleStepCount : LIFECYCLE_STEPS.length;

  // Active policy
  const activePolicies = policies.filter((p) => statusVal(p.status) === 'active');
  const activePolicy = activePolicies.length > 0
    ? activePolicies.sort((a, b) => new Date(b.policy_start_date) - new Date(a.policy_start_date))[0]
    : null;

  // Latest stocking
  const latestStocking = stockingEvents.length > 0
    ? [...stockingEvents].sort((a, b) => new Date(b.stocking_date) - new Date(a.stocking_date))[0]
    : null;
  const doc = latestStocking ? calculateDOC(latestStocking.stocking_date) : 0;
  const targetDOC = 120;
  const docProgress = Math.min((doc / targetDOC) * 100, 100);

  // Risk indicator
  const getRisk = () => {
    if (!latestStocking) return { level: 'none', color: 'gray', label: 'No Crop' };
    if (doc > targetDOC) return { level: 'high', color: 'red', label: 'Overdue' };
    if (doc > 100 && !activePolicy) return { level: 'high', color: 'red', label: 'Uninsured + Late' };
    if (!activePolicy) return { level: 'medium', color: 'orange', label: 'Uninsured' };
    if (doc > 100) return { level: 'medium', color: 'orange', label: 'Harvest Soon' };
    return { level: 'low', color: 'green', label: 'On Track' };
  };
  const risk = getRisk();

  // Credit status badge
  const getCreditBadge = () => {
    if (!activeLoan && creditApps.length === 0) return { label: 'Not Applied', color: 'default', icon: null };
    if (activeLoan?.status === 'disbursed') return { label: 'Active', color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> };
    if (activeLoan?.status === 'approved') return { label: 'Approved', color: 'info', icon: <CheckCircle sx={{ fontSize: 14 }} /> };
    const pending = creditApps.find((a) => a.status === 'pending');
    if (pending) return { label: 'Under Review', color: 'warning', icon: null };
    return { label: `${creditApps.length} app(s)`, color: 'default', icon: null };
  };
  const creditBadge = getCreditBadge();

  // Insurance badge
  const getInsuranceBadge = () => {
    if (activePolicies.length === 0) return { label: 'No Policy', color: 'default' };
    const pendingClaims = claims.filter((c) => statusVal(c.status) === 'pending');
    if (pendingClaims.length > 0) return { label: `${pendingClaims.length} Claim Pending`, color: 'warning' };
    return { label: `${activePolicies.length} Active`, color: 'success' };
  };
  const insBadge = getInsuranceBadge();

  // Settlement projection
  const latestHarvest = harvests.length > 0 ? harvests[0] : null;
  const latestSettlement = settlements.length > 0 ? settlements[0] : null;
  const saleProceeds = latestHarvest?.total_value || 0;
  const totalDeductions = (latestSettlement?.deductions) || 0;
  const netPayout = (latestSettlement?.net_amount) || (saleProceeds - totalDeductions);

  // KYC pending?
  const kycPending =
    kycStatus &&
    (kycStatus.kyc_status === 'not_submitted' ||
      kycStatus.kyc_status === 'pending' ||
      kycStatus.kyc_status === 'rejected' ||
      kycStatus.kyc_status === 'under_review');

  // ── Loading ─────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto px-2 sm:px-4 pb-6">
      {/* Error banner */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Page Header ────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4 pt-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            Command Center
            <Chip
              label={`Cycle ${cycleNumber}`}
              size="small"
              color="primary"
              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
            />
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Welcome back, {user?.full_name || user?.name || 'Farmer'}
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton onClick={loadData} size="small" sx={{ bgcolor: 'grey.100' }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* ══════════════════════════════════════════════════════
          KYC CARD — Only if KYC pending
          ══════════════════════════════════════════════════════ */}
      {kycPending && (
        <div className="mb-4 rounded-2xl border-l-4 border-amber-400 bg-amber-50 p-4 flex items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <VerifiedUser sx={{ color: '#f59e0b', fontSize: 24 }} />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900">KYC Verification Pending</p>
              <p className="text-xs text-amber-700">
                {kycStatus.kyc_status === 'rejected'
                  ? 'Your KYC was rejected. Please resubmit.'
                  : kycStatus.kyc_status === 'under_review'
                    ? 'Your KYC is under review. Please wait.'
                    : 'Complete KYC to unlock all features.'}
              </p>
            </div>
          </div>
          {kycStatus.kyc_status !== 'under_review' && (
            <button
              onClick={() => navigate('/farmer/kyc')}
              className="flex-shrink-0 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold shadow-sm transition-colors"
            >
              {kycStatus.kyc_status === 'rejected' ? 'Resubmit' : 'Complete KYC'}
            </button>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SECTION 1 — Farming Journey Stepper
          ══════════════════════════════════════════════════════ */}
      <div className="rounded-2xl border border-gray-200 bg-white mb-5 shadow-sm overflow-hidden">
        <div className="px-5 pt-4 pb-1 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-gray-800">
              {isRepeatFarmer ? 'Crop Cycle Journey' : 'Your Farming Journey'}
            </h2>
            {isRepeatFarmer && (
              <Chip
                label="Repeat Farmer"
                size="small"
                sx={{
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 22,
                }}
              />
            )}
          </div>
          <Chip
            label={
              isRepeatFarmer
                ? `Cycle ${cycleNumber} – Step ${currentCycleStep} of ${totalSteps}`
                : `Step ${currentStep} of ${totalSteps}`
            }
            color="primary"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
        </div>
        <LifecycleStepper
          currentStep={currentStep}
          stepsData={isRepeatFarmer ? CYCLE_STEPS : undefined}
        />
      </div>

      {/* ══════════════════════════════════════════════════════
          SECTION 2 — Financial Snapshot (4 Cards)
          ══════════════════════════════════════════════════════ */}
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Financial Snapshot
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* ─── Card 1: Credit Status ─── */}
          <div
            className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate('/farmer/credit-escrow')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <AccountBalance sx={{ fontSize: 20, color: '#2563eb' }} />
                </div>
                <span className="text-sm font-semibold text-gray-700">Credit</span>
              </div>
              <Chip
                label={creditBadge.label}
                color={creditBadge.color}
                size="small"
                variant="outlined"
                icon={creditBadge.icon || undefined}
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }}
              />
            </div>
            {activeLoan ? (
              <div className="space-y-1.5">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">
                    ₹{fmt(activeLoan.approved_amount || activeLoan.requested_amount)}
                  </span>
                </div>
                <p className="text-xs text-gray-400">
                  {activeLoan.interest_rate ? `${activeLoan.interest_rate}% p.a.` : ''}{' '}
                  {activeLoan.approved_tenure || activeLoan.tenure_months
                    ? `• ${activeLoan.approved_tenure || activeLoan.tenure_months} months`
                    : ''}
                </p>
              </div>
            ) : (
              <div className="text-center py-2">
                <AccountBalance sx={{ fontSize: 28, color: '#d1d5db' }} />
                <p className="text-xs text-gray-400 mt-1">No active loan</p>
              </div>
            )}
          </div>

          {/* ─── Card 2: Escrow Balance ─── */}
          <div
            className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate('/farmer/credit-escrow')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <AccountBalanceWallet sx={{ fontSize: 20, color: '#059669' }} />
                </div>
                <span className="text-sm font-semibold text-gray-700">Escrow</span>
              </div>
              <Chip
                label={escrowBalance ? 'Active' : 'Inactive'}
                color={escrowBalance ? 'success' : 'default'}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }}
              />
            </div>
            {escrowBalance ? (
              <div className="space-y-1.5">
                <span className="text-2xl font-bold text-gray-900">
                  ₹{fmt(escrowBalance.current_balance || escrowBalance.balance || 0)}
                </span>
                <div className="mt-1">
                  <LinearProgress
                    variant="determinate"
                    value={
                      activeLoan?.approved_amount
                        ? Math.min(
                            ((parseFloat(escrowBalance.current_balance || escrowBalance.balance || 0)) /
                              activeLoan.approved_amount) *
                              100,
                            100,
                          )
                        : 0
                    }
                    sx={{
                      height: 5,
                      borderRadius: 3,
                      bgcolor: 'grey.100',
                      '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#059669' },
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                <AccountBalanceWallet sx={{ fontSize: 28, color: '#d1d5db' }} />
                <p className="text-xs text-gray-400 mt-1">No escrow account</p>
              </div>
            )}
          </div>

          {/* ─── Card 3: Insurance Status ─── */}
          <div
            className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate('/farmer/insurance')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
                  <Shield sx={{ fontSize: 20, color: '#7c3aed' }} />
                </div>
                <span className="text-sm font-semibold text-gray-700">Insurance</span>
              </div>
              <Chip
                label={insBadge.label}
                color={insBadge.color}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }}
              />
            </div>
            {activePolicies.length > 0 ? (
              <div className="space-y-1.5">
                <span className="text-2xl font-bold text-gray-900">
                  ₹{fmt(activePolicies.reduce((sum, p) => sum + (p.sum_insured || 0), 0))}
                </span>
                <p className="text-xs text-gray-400">Sum Insured</p>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-400">Premium</span>
                  <span className="text-gray-700 font-medium">
                    ₹{fmt(activePolicies.reduce((sum, p) => sum + (p.premium_amount || p.net_premium || 0), 0))}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                <Shield sx={{ fontSize: 28, color: '#d1d5db' }} />
                <p className="text-xs text-gray-400 mt-1">No active policy</p>
              </div>
            )}
          </div>

          {/* ─── Card 4: Settlement Projection ─── */}
          <div
            className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate('/farmer/harvest-settlement')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                  <TrendingUp sx={{ fontSize: 20, color: '#0d9488' }} />
                </div>
                <span className="text-sm font-semibold text-gray-700">Settlement</span>
              </div>
              {latestSettlement && (
                <Chip
                  label={statusVal(latestSettlement.status) === 'completed' ? 'Settled' : 'Pending'}
                  color={statusVal(latestSettlement.status) === 'completed' ? 'success' : 'warning'}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }}
                />
              )}
            </div>
            {latestHarvest || latestSettlement ? (
              <div className="space-y-1.5">
                {latestSettlement ? (
                  <>
                    <span className={`text-2xl font-bold ${parseFloat(latestSettlement.net_amount || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      ₹{fmt(latestSettlement.net_amount)}
                    </span>
                    <p className="text-xs text-gray-400">Net Payout</p>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Gross</span>
                      <span className="text-gray-700 font-medium">₹{fmt(latestSettlement.gross_amount)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-2xl font-bold text-gray-900">
                      ₹{fmt(saleProceeds)}
                    </span>
                    <p className="text-xs text-gray-400">Harvest Value</p>
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-2">
                <TrendingUp sx={{ fontSize: 28, color: '#d1d5db' }} />
                <p className="text-xs text-gray-400 mt-1">No settlement yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          SECTION 3 — Crop Status + Next Action
          ══════════════════════════════════════════════════════ */}
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Crop Status
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* ─── DOC Progress + Risk ─── */}
          <div className="lg:col-span-1 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                  <Grass sx={{ fontSize: 20, color: '#0d9488' }} />
                </div>
                <h4 className="text-sm font-semibold text-gray-700">DOC Progress</h4>
              </div>
              {/* Risk Indicator */}
              <div className="flex items-center gap-1">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    risk.color === 'green'
                      ? 'bg-green-500'
                      : risk.color === 'orange'
                        ? 'bg-orange-500'
                        : risk.color === 'red'
                          ? 'bg-red-500'
                          : 'bg-gray-300'
                  }`}
                />
                <span
                  className={`text-xs font-semibold ${
                    risk.color === 'green'
                      ? 'text-green-700'
                      : risk.color === 'orange'
                        ? 'text-orange-700'
                        : risk.color === 'red'
                          ? 'text-red-700'
                          : 'text-gray-400'
                  }`}
                >
                  {risk.label}
                </span>
              </div>
            </div>

            {latestStocking ? (
              <div className="space-y-3">
                {/* DOC number */}
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-gray-900">{doc}</span>
                  <span className="text-sm text-gray-400">/ {targetDOC} DOC</span>
                </div>

                {/* Progress bar */}
                <div>
                  <LinearProgress
                    variant="determinate"
                    value={docProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.100',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background:
                          doc > targetDOC
                            ? '#ef4444'
                            : doc > 100
                              ? 'linear-gradient(90deg, #f59e0b, #f97316)'
                              : 'linear-gradient(90deg, #2563eb, #3b82f6)',
                      },
                    }}
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-[11px] text-gray-400">
                      {Math.max(0, targetDOC - doc)} days remaining
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {docProgress >= 100 ? 'Harvest Ready' : `${Math.round(docProgress)}%`}
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">Species</span>
                    <span className="text-gray-700 font-medium">{latestStocking.species || 'Vannamei'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">Stocking Date</span>
                    <span className="text-gray-700 font-medium">
                      {new Date(latestStocking.stocking_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">Insured</span>
                    <span className={`font-medium ${activePolicy ? 'text-green-600' : 'text-amber-600'}`}>
                      {activePolicy ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-5 text-center">
                <Grass sx={{ fontSize: 36, color: '#d1d5db' }} />
                <p className="text-xs text-gray-400 mt-2">No active crop cycle</p>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1.5, textTransform: 'none', borderRadius: 2 }}
                  onClick={() => navigate('/farmer/stocking-details')}
                >
                  Start Stocking
                </Button>
              </div>
            )}
          </div>

          {/* ─── Next Required Action ─── */}
          <div className="lg:col-span-2">
            <NextActionCard currentStep={currentStep} />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          SECTION 4 — Alerts Summary
          ══════════════════════════════════════════════════════ */}
      <div className="mb-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Alerts
        </h3>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                <NotificationsActive sx={{ fontSize: 20, color: '#ef4444' }} />
              </div>
              <h4 className="text-sm font-semibold text-gray-700">Recent Alerts</h4>
            </div>
            {alerts.length > 0 && (
              <Chip
                label={`${alerts.length} new`}
                color="error"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }}
              />
            )}
          </div>

          {alertsLoading ? (
            <div className="flex justify-center py-4">
              <CircularProgress size={24} />
            </div>
          ) : alerts.length > 0 ? (
            <>
              <div className="space-y-2">
                {alerts.slice(0, 3).map((alert, idx) => (
                  <div
                    key={alert.id || idx}
                    className="flex items-start gap-2 p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => {
                      // Trigger bell panel opening — for now navigate to alerts
                      // In production, dispatch event to open notification panel
                    }}
                  >
                    {(() => {
                      const sev = (alert.severity || '').toLowerCase();
                      if (sev === 'critical' || sev === 'high')
                        return <ErrorOutline sx={{ fontSize: 16, color: '#ef4444', mt: 0.2 }} />;
                      if (sev === 'warning' || sev === 'medium')
                        return <WarningAmber sx={{ fontSize: 16, color: '#f59e0b', mt: 0.2 }} />;
                      if (sev === 'success')
                        return <CheckCircleOutline sx={{ fontSize: 16, color: '#16a34a', mt: 0.2 }} />;
                      return <InfoOutlined sx={{ fontSize: 16, color: '#3b82f6', mt: 0.2 }} />;
                    })()}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">
                        {alert.title || alert.message || 'Alert'}
                      </p>
                      {alert.message && alert.title && (
                        <p className="text-[11px] text-gray-400 truncate">{alert.message}</p>
                      )}
                    </div>
                    <Chip
                      label={alert.severity || 'info'}
                      color={
                        (() => {
                          const s = (alert.severity || '').toLowerCase();
                          if (s === 'critical' || s === 'high') return 'error';
                          if (s === 'warning' || s === 'medium') return 'warning';
                          if (s === 'success') return 'success';
                          return 'info';
                        })()
                      }
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 18, textTransform: 'capitalize' }}
                    />
                  </div>
                ))}
              </div>
              {alerts.length > 3 && (
                <div className="mt-3 text-center">
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                    sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                    onClick={() => {
                      // Open bell panel / notification drawer
                      // Fallback: scroll up or dispatch custom event
                      const bellButton = document.querySelector('[data-testid="notification-bell"]');
                      if (bellButton) {
                        bellButton.click();
                      }
                    }}
                  >
                    View all {alerts.length} alerts
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-3 py-2">
              <CheckCircleOutline sx={{ fontSize: 20, color: '#d1d5db' }} />
              <p className="text-sm text-gray-400">No pending alerts — you're all caught up!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FarmerDashboard;
